import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../configs/firebase';

class AdminConfig {
  constructor() {
    this.cache = null;
    this.cacheExpiry = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
  }

  // Check if admin mode is enabled
  isAdminMode() {
    return process.env.REACT_APP_ADMIN_MODE === 'true';
  }

  // Get admin configuration from Firestore
  async getAdminConfig() {
    if (!this.isAdminMode()) {
      throw new Error('Admin mode not enabled');
    }

    // Check if cache is still valid
    if (this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      return this.cache;
    }

    try {
      const configDoc = await getDoc(doc(db, 'config', 'admin'));
      
      if (configDoc.exists()) {
        this.cache = configDoc.data();
        this.cacheExpiry = Date.now() + this.cacheTimeout;
        return this.cache;
      } else {
        // Create default config if it doesn't exist
        const defaultConfig = await this.createDefaultConfig();
        return defaultConfig;
      }
    } catch (error) {
      console.error('Error fetching admin config:', error);
      throw error;
    }
  }

  // Get admin email whitelist
  async getAdminEmails() {
    const config = await this.getAdminConfig();
    return config?.adminEmails || [];
  }

  // Check if email is admin
  async isAdminEmail(email) {
    const adminEmails = await this.getAdminEmails();
    return adminEmails.includes(email?.toLowerCase());
  }

  // Add admin email
  async addAdminEmail(email) {
    if (!this.isAdminMode()) {
      throw new Error('Admin mode not enabled');
    }

    try {
      const config = await this.getAdminConfig();
      const currentEmails = config.adminEmails || [];
      
      if (!currentEmails.includes(email.toLowerCase())) {
        const updatedEmails = [...currentEmails, email.toLowerCase()];
        
        await updateDoc(doc(db, 'config', 'admin'), {
          adminEmails: updatedEmails,
          updatedAt: new Date()
        });
        
        // Clear cache
        this.cache = null;
        this.cacheExpiry = null;
        
        return true;
      }
      return false; // Email already exists
    } catch (error) {
      console.error('Error adding admin email:', error);
      throw error;
    }
  }

  // Remove admin email
  async removeAdminEmail(email) {
    if (!this.isAdminMode()) {
      throw new Error('Admin mode not enabled');
    }

    try {
      const config = await this.getAdminConfig();
      const currentEmails = config.adminEmails || [];
      const updatedEmails = currentEmails.filter(e => e !== email.toLowerCase());
      
      await updateDoc(doc(db, 'config', 'admin'), {
        adminEmails: updatedEmails,
        updatedAt: new Date()
      });
      
      // Clear cache
      this.cache = null;
      this.cacheExpiry = null;
      
      return true;
    } catch (error) {
      console.error('Error removing admin email:', error);
      throw error;
    }
  }

  // Create default configuration
  async createDefaultConfig() {
    const defaultConfig = {
      adminEmails: [
        'admin@yourdomain.com' // Add your initial admin email here
      ],
      maxSessions: 10,
      sessionTimeout: 7200000, // 2 hours
      isEnabled: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    try {
      await setDoc(doc(db, 'config', 'admin'), defaultConfig);
      this.cache = defaultConfig;
      this.cacheExpiry = Date.now() + this.cacheTimeout;
      return defaultConfig;
    } catch (error) {
      console.error('Error creating default admin config:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const adminConfig = new AdminConfig();